import React, { useEffect, useState, Children, lazy } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Heart, Apple, Moon, Briefcase, UserCheck, FileText, Home as HomeIcon, Key, Truck, Plane, Calendar, ShoppingBag, ArrowRight } from 'lucide-react';
// Animation variants
const fadeUpVariants = {
  hidden: {
    opacity: 0,
    y: 30
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.8,
      ease: 'easeOut'
    }
  }
};
const staggerContainer = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};
const SERVICES_DATA = [{
  id: 'wellness',
  category: 'Wellness & Lifestyle',
  icon: Heart,
  items: [{
    title: 'Fitness Coaching',
    description: "Personalised workout plans, virtual and in-person sessions, accountability tracking. Whether you're starting fresh or levelling up, we meet you where you are.",
    icon: Heart
  }, {
    title: 'Nutrition & Meal Prep',
    description: 'Custom meal plans, grocery lists, and weekly prep services. Fuel your body without the mental load of figuring out what to eat.',
    icon: Apple
  }, {
    title: 'Rest & Recovery',
    description: "Sleep optimisation, stress management techniques, and recovery protocols. Because rest isn't lazy — it's strategic.",
    icon: Moon
  }]
}, {
  id: 'professional',
  category: 'Professional Support',
  icon: Briefcase,
  items: [{
    title: 'Virtual Assistant',
    description: 'Inbox management, scheduling, research, and admin tasks handled by a dedicated professional who understands your pace.',
    icon: UserCheck
  }, {
    title: 'Executive Support',
    description: 'High-level strategic support for C-suite women. Board prep, travel coordination, stakeholder management.',
    icon: Briefcase
  }, {
    title: 'Small Business Compliance',
    description: 'CIPC registrations, tax compliance, B-BBEE certificates, and regulatory filings. Stay legal without the headache.',
    icon: FileText
  }]
}, {
  id: 'home',
  category: 'Home & Property',
  icon: HomeIcon,
  items: [{
    title: 'Cleaning Services',
    description: 'Regular deep cleans, move-in/move-out cleaning, and specialised services. Your space, spotless and stress-free.',
    icon: HomeIcon
  }, {
    title: 'Property / Airbnb Management',
    description: 'End-to-end management of your rental properties. Guest communication, maintenance, pricing optimisation.',
    icon: Key
  }, {
    title: 'Relocation & Settling-In',
    description: 'Moving to a new city or country? We handle logistics, home setup, school research, and local orientation.',
    icon: Truck
  }]
}, {
  id: 'life',
  category: 'Life Management',
  icon: Calendar,
  items: [{
    title: 'Travel Planning',
    description: 'Curated itineraries, flight and hotel bookings, visa support, and travel insurance. Adventure without the admin.',
    icon: Plane
  }, {
    title: 'Event Planning',
    description: 'From intimate dinners to corporate functions. Venue sourcing, vendor management, day-of coordination.',
    icon: Calendar
  }, {
    title: 'Personal Errand & Concierge',
    description: 'Gift shopping, appointment booking, prescription pickups, and anything else on your to-do list.',
    icon: ShoppingBag
  }]
}];
export function Services() {
  const [activeTab, setActiveTab] = useState(SERVICES_DATA[0].id);
  const [isSticky, setIsSticky] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      // Adjust offset based on hero height + navbar
      setIsSticky(window.scrollY > window.innerHeight * 0.6 - 80);
      // Update active tab based on scroll position
      const sections = SERVICES_DATA.map(s => document.getElementById(s.id));
      const scrollPosition = window.scrollY + 200; // Offset for sticky header
      for (let i = sections.length - 1; i >= 0; i--) {
        const section = sections[i];
        if (section && section.offsetTop <= scrollPosition) {
          setActiveTab(SERVICES_DATA[i].id);
          break;
        }
      }
    };
    window.addEventListener('scroll', handleScroll, {
      passive: true
    });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      const y = element.getBoundingClientRect().top + window.scrollY - 140; // Adjust for sticky header
      window.scrollTo({
        top: y,
        behavior: 'smooth'
      });
    }
  };
  return <main className="flex flex-col min-h-screen bg-warmwhite" data-id="element-655" data-mp="7ayzc7zkda6f">
      {/* HERO BANNER */}
      <section className="relative h-[60vh] min-h-[400px] flex items-center justify-center overflow-hidden bg-plum pt-20" data-id="element-656" data-mp="cableqim2d7y">
        <div className="absolute inset-0 z-0" data-id="element-657" data-mp="h58svt08seiq">
          <img src="https://images.unsplash.com/photo-1600880292203-757bb62b4baf?q=80&w=2070&auto=format&fit=crop" alt="Organized workspace" className="w-full h-full object-cover opacity-20" data-id="element-658" data-mp="l3euynq4y5xb" />
          <div className="absolute inset-0 bg-gradient-to-b from-plum/80 via-plum/90 to-plum" data-id="element-659" data-mp="3pksimi1pfet1" />
        </div>

        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-660" data-mp="3tmmdg9p7xola">
          <motion.h1 className="font-display text-4xl md:text-6xl lg:text-7xl font-bold text-white mb-6 leading-tight" initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8
        }} data-id="element-661" data-mp="2bm0spzbyeo0">
            Everything you need.
            <br data-id="element-662" data-mp="3jufywk1h6c3k" />
            One trusted team.
          </motion.h1>
          <motion.p className="text-lg md:text-xl text-warmwhite/80 max-w-2xl mx-auto font-body leading-relaxed" initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.3,
          duration: 0.8
        }} data-id="element-663" data-mp="3jtofe7l5k4tn">
            From wellness to business support, we handle the details so you can
            focus on what matters.
          </motion.p>
        </div>
      </section>

      {/* STICKY TABS */}
      <div className={`sticky top-[72px] md:top-[88px] z-40 transition-all duration-300 ${isSticky ? 'bg-warmwhite/90 backdrop-blur-md shadow-sm py-4' : 'bg-warmwhite py-6'}`} data-id="element-664" data-mp="28dgba1q2olfv">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-665" data-mp="iwm6yaxt5nmv">
          <div className="flex overflow-x-auto hide-scrollbar gap-2 md:gap-8 pb-2 md:pb-0 md:justify-center" data-id="element-666" data-mp="np5hez1782ng">
            {SERVICES_DATA.map(category => <button key={category.id} onClick={() => scrollToSection(category.id)} className={`relative whitespace-nowrap px-4 py-2 text-sm md:text-base font-medium transition-colors ${activeTab === category.id ? 'text-fuchsia' : 'text-charcoal/70 hover:text-charcoal'}`} data-id="element-667" data-mp="9vnxhd4lonww">
                {category.category}
                {activeTab === category.id && <motion.div layoutId="activeTabIndicator" className="absolute bottom-0 left-0 right-0 h-0.5 bg-fuchsia rounded-full" initial={false} transition={{
              type: 'spring',
              stiffness: 300,
              damping: 30
            }} data-id="element-668" data-mp="xmth83dnw0ch" />}
              </button>)}
          </div>
        </div>
      </div>

      {/* SERVICES GRID */}
      <div className="py-12 md:py-24" data-id="element-669" data-mp="3ycuhigsf64v">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col gap-24" data-id="element-670" data-mp="3ze5xo1ptun4">
          {SERVICES_DATA.map(category => {
          const CategoryIcon = category.icon;
          return <section key={category.id} id={category.id} className="scroll-mt-40" data-id="element-671" data-mp="wo1uptlob25f">
                <motion.div initial="hidden" whileInView="visible" viewport={{
              once: true,
              margin: '-100px'
            }} variants={fadeUpVariants} className="flex items-center gap-4 mb-12" data-id="element-672" data-mp="mve095i61a9v">
                  <div className="w-12 h-12 rounded-xl bg-plum/10 flex items-center justify-center" data-id="element-673" data-mp="1ajnhrv8cjbv7">
                    <CategoryIcon className="w-6 h-6 text-plum" data-id="element-674" data-mp="1hyzk417ju9gq" />
                  </div>
                  <h2 className="font-display text-3xl md:text-4xl font-bold text-charcoal" data-id="element-675" data-mp="1mw813ygyo7ge">
                    {category.category}
                  </h2>
                </motion.div>

                <motion.div variants={staggerContainer} initial="hidden" whileInView="visible" viewport={{
              once: true,
              margin: '-50px'
            }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-676" data-mp="1wvqkodsylr7x">
                  {category.items.map((service, index) => {
                const ServiceIcon = service.icon;
                return <motion.div key={index} variants={fadeUpVariants} className="group bg-white rounded-2xl p-8 shadow-sm border-l-4 border-fuchsia hover:-translate-y-1 hover:shadow-md transition-all duration-300 flex flex-col h-full" data-id="element-677" data-mp="2khn8xu7xlw0w" data-mpi={index}>
                        <ServiceIcon className="w-8 h-8 text-plum mb-6" data-id="element-678" data-mp="2okzepni62im0" />
                        <h3 className="font-display text-xl font-bold text-charcoal mb-3" data-id="element-679" data-mp="2oiwgig6v1j67">
                          {service.title}
                        </h3>
                        <p className="text-charcoal/70 font-body text-sm leading-relaxed mb-8 flex-grow" data-id="element-680" data-mp="2y9r2djg72gr4">
                          {service.description}
                        </p>

                        <div className="flex items-center justify-between mt-auto pt-6 border-t border-charcoal/5" data-id="element-681" data-mp="37axny6xpahr8">
                          <Link to="/contact" className="flex items-center gap-2 text-sm font-medium text-fuchsia group-hover:text-fuchsia-light transition-colors" data-id="element-682" data-mp="3c8z7su2yal3r">
                            Learn more <ArrowRight className="w-4 h-4" data-id="element-683" data-mp="1achywvvixmmq" />
                          </Link>
                          <Link to="/contact" className="px-4 py-2 bg-plum text-white text-xs font-medium rounded-full hover:bg-plum-light transition-colors" data-id="element-684" data-mp="15mbaox4xc450">
                            Book a Call
                          </Link>
                        </div>
                      </motion.div>;
              })}
                </motion.div>
              </section>;
        })}
        </div>
      </div>

      {/* BOTTOM CTA */}
      <section className="py-24 bg-plum relative overflow-hidden" data-id="element-685" data-mp="1jc4ydc4p0id5">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-fuchsia/20 via-transparent to-transparent" data-id="element-686" data-mp="1eev33fumhche" />
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10" data-id="element-687" data-mp="7w6k8tunr652">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeUpVariants} data-id="element-688" data-mp="2xvcconxb5ph">
            <h2 className="font-display text-4xl md:text-5xl font-bold text-white mb-6" data-id="element-689" data-mp="2v5xhf6vfr9co">
              Not sure which service fits?
            </h2>
            <p className="text-warmwhite/80 text-lg mb-10 font-body" data-id="element-690" data-mp="2m5iu61ywvk6j">
              Book a free clarity call — we'll help you figure out exactly what
              you need.
            </p>
            <Link to="/contact" className="inline-block px-8 py-4 bg-gold text-charcoal font-medium rounded-full hover:bg-gold-light transition-colors shadow-lg" data-id="element-691" data-mp="2c7mdkun7fx11">
              Book Your Free Call
            </Link>
          </motion.div>
        </div>
      </section>
    </main>;
}